import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMenuItems } from '../lib/utils';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  Plus,
  FileText,
  CheckCircle,
  CreditCard,
  Calendar,
  BarChart3,
  Lock,
  Menu,
  X,
  LogOut,
  User,
  ChevronRight,
  Building2
} from 'lucide-react';

const iconMap = {
  LayoutDashboard,
  Plus,
  FileText,
  CheckCircle,
  CreditCard,
  Calendar,
  BarChart3,
  Lock,
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = getMenuItems(user?.user_position || '');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ item }) => {
    const Icon = iconMap[item.icon] || FileText;
    const isActive = location.pathname === item.path || 
                     (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`}
        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
      >
        <Icon className="w-5 h-5" />
        <span>{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </Button>
        <div className="flex-1 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-primary mr-2" />
          <span className="font-bold text-lg">MyTour</span>
        </div>
        <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="mobile-logout-btn">
          <LogOut className="w-5 h-5" />
        </Button>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <Building2 className="w-8 h-8 text-primary mr-3" />
          <div>
            <h1 className="font-bold text-lg leading-none">MyTour</h1>
            <p className="text-xs text-slate-500">BBEST REALTY</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden ml-auto"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate" data-testid="user-name">{user?.user_name}</p>
              <p className="text-xs text-slate-500 truncate" data-testid="user-position">{user?.user_position}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-200px)]">
          {menuItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
